import React, { createContext, FC, MouseEvent, useEffect, useMemo } from 'react'
import arrowTriangle from '@assets/images/arrow-triangle.svg'
import settings from '@assets/images/setings.svg'
import { Button } from '@microservices/wiskey-react-components'
import { Box, Grid } from '@mui/material'

import { HandleSetOpenedContainersParams } from '@pages/ConfiguredEntity/types'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { MenuPortal } from '@components/MenuPortal'
import { RedirectToConfigButton } from '@components/RedirectToConfigButton'

import { updateLinkTitle } from '@redux/reducers/formHistory.reducer'

import { PrintTrigger } from '@features/print-forms/components/PrintTrigger'

import { useAppDispatch, useContextMenu } from '@hooks'
import {
  buttonStylesByMode,
  getInitialPositionWindow,
  ReturnTransformCommonParametersToValues,
} from '@helpers'
import {
  ENTITY,
  FLEXIBLE,
  FORM_TYPE,
  MENU_POINT_TYPES,
  OuterDataType,
  PreFillType,
  WINDOW_FORM_CONFIG_LINE_HEIGHT,
} from '@constants'

import {
  FormContainerEnriched,
  FormType,
  LinkType,
  LinkViewHistory,
  MenuPointOptionType,
  ObjectDataRecord,
  PassedFormData,
} from '../../types'
import { AppLoader } from '../AppLoader'

import { DisplayFormContent } from './components/DisplayFormContent'
import { BreadcrumbsForm } from './components'
import { getContextValue } from './helpers'
import { getFormTitle } from './helpers'
import { useDisplayForm } from './hooks'
import { FormContextType, FormStateType } from './types'

export type DisplayFormProps = {
  passedParams: PassedFormData
  onChangeDirty?: (value: boolean) => void
  onSetWindowTitle?: (title: string | null) => void
  onBreadcrumbsMainLinkClick: (link?: LinkViewHistory) => void
  onObjectDataRecordSuccessCreating?: (links: LinkType[], data: ObjectDataRecord) => void
  handleNavigateToDefaultPage: () => void
  handleChangeFormCode: (formCode: string, verifyFormCode: string) => void
  handleRemoveSamePageForm: (formDataId?: number, isDirty?: boolean) => void
  handleUpdateSamePageFormData: (
    oldObjectId: string,
    newFormCode: string,
    objectCode: string,
    objectId: string
  ) => void
  handleCloseCurrentFormTab: () => void
  entityType: ENTITY
  onSetOpenedContainersInStaticForms: (params: HandleSetOpenedContainersParams) => void
  samePageFormsData: PassedFormData[] | undefined
  selectedRowIds: string[] | undefined
  skipHotKeyPress?: boolean
  dialogId?: string
  isFormDialogWindow?: boolean
  windowHeight?: number
  windowWidth?: number
  preFill?: PreFillType
  dialogMeta?: any
  outerData?: OuterDataType
  resetFieldsAfterCreate?: boolean
  isSectionWindow?: boolean
  isIframeForm?: boolean
  instanceId: string
}

export type DisplayFormExtendedProps = {
  formConfig: FormType
  formContainersConfig: FormContainerEnriched[]
  initialCommonParameters?: ReturnTransformCommonParametersToValues
  //defaultValues: ObjectDataRecord
}

export const FormContext = createContext<FormContextType>({} as FormContextType)
export const FormStateContext = createContext<FormStateType>({} as FormStateType)

export const DisplayForm: FC<DisplayFormProps & DisplayFormExtendedProps> = ({ ...props }) => {
  const {
    passedParams: { globalId },
    windowWidth,
    onSetWindowTitle,
    dialogId,
    isFormDialogWindow = false,
    isSectionWindow,
  } = props

  const dispatch = useAppDispatch()

  const { data, state, handlers } = useDisplayForm(props)

  const { form, links, withPassedParams, isViewType, isDirty, methods, dirtyFields, isValid } = data

  const {
    currentRow,
    isLoadingRow,
    currentLink,
    isPendingCreateOrUpdateForm,
    isInitialValuesForCreateFormInitialized,
  } = state

  const { handleOpenLink } = handlers

  const formTitle = useMemo(() => {
    if (currentLink.event === FORM_TYPE.CREATE && form) {
      return getFormTitle(form, {})
    }

    if (form && currentRow && !isLoadingRow) {
      return getFormTitle(form, currentRow)
    }

    return null
  }, [form, currentRow, isLoadingRow])

  useEffect(() => {
    if (!isSectionWindow) {
      onSetWindowTitle?.(formTitle)
      if (dialogId && formTitle) {
        dispatch(updateLinkTitle({ id: dialogId, title: formTitle }))
      }
    }
  }, [formTitle, isSectionWindow])

  const contextValue: FormContextType = useMemo(
    () =>
      getContextValue({
        data,
        handlers,
        state,
        formTitle,
        formProps: props,
      }),
    [data, handlers, state, formTitle, props]
  )

  const formStateValue: FormStateType = useMemo(
    () => ({
      isDirty,
      isViewType,
      dirtyFields,
      isFormDialogWindow,
    }),
    [isDirty, isViewType, dirtyFields, isFormDialogWindow]
  )

  const {
    onClickLink,
    onExecuteCommand,
    onOpenContextMenu,
    onCloseContextMenu,
    position,
    onExecuteAction,
    printFormVouPayload,
    onClosePrintFormVou,
  } = useContextMenu({ dialogId: globalId, variantPosition: 'element' })
  const menuIgnoreSelectorClassName = 'menuButton'

  const handleClickContextMenuItem = (data: {
    metaData: {
      row?: ObjectDataRecord
      menuItem: MenuPointOptionType
      event: MouseEvent<HTMLDivElement>
    }
  }) => {
    if (data.metaData.menuItem.menuPointType === MENU_POINT_TYPES.LINK) {
      const position = getInitialPositionWindow(data.metaData.event)
      onClickLink(data.metaData.menuItem, position)

      return
    }

    if (data.metaData?.menuItem.menuPointType === MENU_POINT_TYPES.COMMAND) {
      onExecuteCommand(data.metaData)

      return
    }

    if (data.metaData?.menuItem.menuPointType === MENU_POINT_TYPES.ACTION) {
      onExecuteAction(data.metaData)
    }
  }

  const handleClickButtonContextMenu = (event: MouseEvent<HTMLElement>) => {
    if (!position) {
      onOpenContextMenu(event)

      return
    }

    onCloseContextMenu()
  }

  return (
    <FormContext.Provider value={contextValue}>
      <FormStateContext.Provider value={formStateValue}>
        <Grid container justifyContent={'space-between'} px={1}>
          <Grid item>
            {props.dialogId && links && (
              <BreadcrumbsForm
                dialogId={props.dialogId}
                links={links}
                maxItems={4}
                openLink={handleOpenLink}
                windowWidth={windowWidth}
              />
            )}
          </Grid>
          <RedirectToConfigButton code={form?.code} sx={{ p: 0, mt: 1.25, mr: 0.5 }} type='form' />
          <Grid item pr={0.5}>
            {form?.contextMenu && (
              <MenuPortal
                items={form?.contextMenu?.menuPoints}
                position={position}
                selectorsIgnore={[menuIgnoreSelectorClassName]}
                dataTransfer={{
                  changes: {},
                  config: {},
                  data: {},
                  metaData: { srcObj: currentRow },
                }}
                onClose={onCloseContextMenu}
                onItemClick={handleClickContextMenuItem}
              />
            )}
            <Button
              className={menuIgnoreSelectorClassName}
              disabled={!form?.contextMenu}
              sx={theme => ({
                ...buttonStylesByMode(theme),
                p: '0 !important',
                height: 21,
                width: 54,
                minWidth: 54,
                justifyContent: 'space-between',
                pl: '10px',
                pr: '6px',
                mt: 1.5,
                ...(position && {
                  background: theme.palette.background.buttonHovered,
                  boxShadow: theme.palette.boxShadow.buttonHovered,
                }),
              })}
              onClick={handleClickButtonContextMenu}
            >
              <CustomSvgIcon
                className={menuIgnoreSelectorClassName}
                imgClassName={menuIgnoreSelectorClassName}
                imgStyle={{}}
                src={settings}
                sx={{ fontSize: '1rem', ml: '8px' }}
              />
              <CustomSvgIcon
                className={menuIgnoreSelectorClassName}
                imgClassName={menuIgnoreSelectorClassName}
                src={arrowTriangle}
                sx={{ fontSize: '1rem', mr: '6px' }}
              />
            </Button>
          </Grid>
        </Grid>
        {printFormVouPayload && (
          <PrintTrigger
            config={printFormVouPayload.config}
            records={printFormVouPayload.records}
            onDone={onClosePrintFormVou}
          />
        )}
        {!withPassedParams ? (
          <PageContentLayout
            isBreadcrumbs
            height={isSectionWindow ? `calc(100% - ${WINDOW_FORM_CONFIG_LINE_HEIGHT}px)` : '100%'}
          >
            <Box height='100%' position={'relative'} pt={1} px={1} sx={FLEXIBLE}>
              {isInitialValuesForCreateFormInitialized && (
                <DisplayFormContent
                  isDirty={isDirty}
                  isSectionWindow={isSectionWindow}
                  isValid={isValid}
                  methods={methods}
                />
              )}
              {(isPendingCreateOrUpdateForm ||
                isLoadingRow ||
                !isInitialValuesForCreateFormInitialized) && (
                <AppLoader
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  }}
                />
              )}
            </Box>
          </PageContentLayout>
        ) : (
          <DisplayFormContent isDirty={isDirty} methods={methods} />
        )}
      </FormStateContext.Provider>
    </FormContext.Provider>
  )
}
